import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Bookmark, Briefcase, Star, Building2, Send, Award, Search, PlusCircle, Radar, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import TrialBanner from "@/components/TrialBanner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAgency } from "@/lib/roles";

function Stat({ icon: Icon, label, value, to, testid }) {
  const body = (
    <div data-testid={testid} className="liquid-glass rounded-2xl p-5 hover:border-teal-500/30 transition-colors">
      <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-400 mb-3"><Icon size={18} /></div>
      <div className="font-display font-bold text-3xl text-slate-50">{value ?? 0}</div>
      <div className="text-xs text-slate-400 mt-1">{label}</div>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

function Action({ icon: Icon, label, to, testid }) {
  return (
    <Link to={to} data-testid={testid} className="flex items-center gap-3 rounded-xl liquid-glass p-4 hover:border-teal-500/30 transition-colors">
      <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center text-slate-900"><Icon size={17} /></span>
      <span className="text-sm font-medium text-slate-200">{label}</span>
    </Link>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const agency = isAgency(user?.role);
  const [stats, setStats] = useState({});
  const [saved, setSaved] = useState([]);

  useEffect(() => {
    api.get("/employer/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/saved-candidates").then((r) => setSaved(r.data.slice(0, 5))).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen wo-bg">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 pb-28 lg:pb-8">
        <div className="mb-6">
          <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-cyan-400 border border-cyan-500/25 rounded-full px-4 py-1.5 mb-3">
            {agency ? "Agency workspace" : "Hiring workspace"}
          </span>
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-slate-50" data-testid="dashboard-title">Welcome back, {user?.name}</h1>
          <p className="text-slate-400 mt-2">{agency ? "Source talent, manage client companies and track placements." : "Search candidates, review applicants and fill your roles faster."}</p>
        </div>

        <TrialBanner />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Stat icon={Users} label="Candidates in talent pool" value={stats.talent_pool} to="/talent" testid="stat-talent-pool" />
          <Stat icon={Bookmark} label="Saved candidates" value={stats.saved_candidates} to="/saved-candidates" testid="stat-saved" />
          <Stat icon={Briefcase} label="Active job postings" value={stats.jobs} to="/applicants" testid="stat-jobs" />
          <Stat icon={Star} label="Shortlisted applicants" value={stats.shortlisted} to="/applicants" testid="stat-shortlisted" />
          {agency && <>
            <Stat icon={Building2} label="Client companies" value={stats.clients} to="/clients" testid="stat-clients" />
            <Stat icon={Send} label="Candidate submissions" value={stats.submissions} to="/submissions" testid="stat-submissions" />
            <Stat icon={Award} label="Placements" value={stats.placements} to="/submissions" testid="stat-placements" />
            <Stat icon={Users} label="Total applicants" value={stats.applicants} to="/applicants" testid="stat-applicants" />
          </>}
        </div>

        <h2 className="font-display font-semibold text-slate-200 mb-3">Quick actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          <Action icon={Search} label="Search talent" to="/talent" testid="action-talent" />
          <Action icon={PlusCircle} label="Post a job" to="/post-job" testid="action-post-job" />
          <Action icon={Radar} label="Applicant radar" to="/applicants" testid="action-applicants" />
          {agency
            ? <Action icon={Building2} label="Manage clients" to="/clients" testid="action-clients" />
            : <Action icon={Bookmark} label="Talent pool" to="/saved-candidates" testid="action-saved" />}
          <Action icon={Sparkles} label={user?.plan ? "Manage plan" : "Upgrade plan"} to="/upgrade" testid="action-upgrade" />
        </div>

        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-semibold text-slate-200">Recently saved candidates</h2>
          <Link to="/saved-candidates" className="text-sm text-teal-400 hover:underline">View all</Link>
        </div>
        {saved.length === 0 ? (
          <div className="liquid-glass rounded-2xl p-8 text-center text-slate-500" data-testid="dashboard-saved-empty">
            No saved candidates yet — head to <Link to="/talent" className="text-teal-400 hover:underline">Talent Search</Link> to start building your pool.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {saved.map((c) => (
              <Link key={c.id} to="/saved-candidates" className="liquid-glass rounded-xl p-4 flex items-center gap-3 hover:border-teal-500/30 transition-colors">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-blue-600 flex items-center justify-center text-sm font-bold text-slate-900">{c.name?.[0]?.toUpperCase()}</div>
                <div className="min-w-0"><div className="font-medium text-slate-100 truncate">{c.name}</div><div className="text-xs text-slate-400 truncate">{c.headline}</div></div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
